import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import serialize from 'form-serialize';

import { bemGenerator } from '@prisjakt/ui-base';
import { AppContext } from '@prisjakt/ui-app-context';
import Button from '@prisjakt/ui-button';
import StoreLogo, { VARIANTS as LOGO_VARIANTS } from '@prisjakt/ui-store-logo';

import { TextInput } from '@prisjakt/ui-inputs';
import CheckIcon from 'mdi-react/CheckCircleIcon';
import Modal from '../../../components/modal';
import SubmitButton from '../../account/components/submit-button';
import actions from './actions';

import './index.scss';
import './sagas';

const styles = bemGenerator('pj-soty-vote-popup', [
  'title',
  'subtitle',
  'logo',
  'vote-shop',
  'buttons',
  'disclaimer',
  'already-voted-notice',
  'form',
  'email-input',
  'check-icon',
  'thanks',
  'nomination-logo',
  'error',
]);

const errorIsEmailAlreadyRegistered = error =>
  error === 'registration.email-in-use';

class VotePopup extends React.PureComponent {
  handleSubmit(e) {
    e.preventDefault();
    const { email = '' } = serialize(e.target, { hash: true });

    const { sotyVote, token, shop, nominationCategory, user } = this.props;

    sotyVote(token, email, user, nominationCategory.id, shop.id);
  }

  hasUserVotedInThisCategory() {
    const { history = [], nominationCategory } = this.props;
    return (
      history.filter(vote => vote.category.id === nominationCategory.id)
        .length > 0
    );
  }

  getModeVoting() {
    const {
      shop,
      nominationCategory,
      voting,
      year,
      user,
      errors,
      token,
      charity,
    } = this.props;
    const { t, market } = this.context;

    return (
      <React.Fragment>
        <div className={styles['vote-shop']}>
          {t('campaigns', 'shop-of-the-year.vote-for-shop-in-category', {
            shopName: shop.name,
          })}
        </div>
        <h1 className={styles.title}>
          {nominationCategory.name} {year}
        </h1>

        <img
          className={styles['nomination-logo']}
          src={nominationCategory.logo}
          alt={nominationCategory.name}
        />

        {this.hasUserVotedInThisCategory() && (
          <div className={styles['already-voted-notice']}>
            {t('campaigns', 'shop-of-the-year.already-voted-in-category')}
          </div>
        )}

        <form
          className={styles.form}
          method="post"
          onSubmit={e => this.handleSubmit(e)}
        >
          {!user.isAuthenticated && (
            <div>
              <TextInput
                name="email"
                placeholder={t('account', 'input.email')}
                className={styles['email-input']}
                defaultValue={token ? token.email : null}
                warning={errors.email && errors.email.length > 0}
                helper={errors.email ? t('errors', errors.email[0]) : null}
              />
              <div className={styles.disclaimer}>
                {t('campaigns', 'shop-of-the-year.vote-gdpr-text')}{' '}
                <Link to={market.privacySettings.responsive.privacyUrl}>
                  {t('common', 'cookie-banner.privacy-policy')}
                </Link>
              </div>
            </div>
          )}

          <div className={styles.error}>
            {errors.form && t('errors', errors.form[0])}{' '}
            {errors.form && errorIsEmailAlreadyRegistered(errors.form[0]) && (
              <Link to="/login">
                {t('campaigns', 'shop-of-the-year.login-to-vote')}
              </Link>
            )}
          </div>

          <div className={styles.buttons}>
            <SubmitButton
              color="primary"
              loading={voting}
              text={t('campaigns', 'shop-of-the-year.vote')}
            />
            {charity && (
              <div className={styles.disclaimer}>{charity.description}</div>
            )}
          </div>
        </form>
      </React.Fragment>
    );
  }

  getModeHasVoted() {
    const { t } = this.context;
    const { shop, nominationCategory } = this.props;
    return (
      <React.Fragment>
        <div className={styles['vote-shop']}>
          {t('campaigns', 'shop-of-the-year.voted-for-shop-in-category', {
            shopName: shop.name,
            categoryName: nominationCategory.name,
          })}
        </div>
        <CheckIcon size={130} className={styles['check-icon']} />
        <div className={styles.thanks}>
          {t('campaigns', 'shop-of-the-year.thanks-for-vote')}
        </div>
        <div className={styles.buttons}>
          <Button
            color="primary"
            style={{ width: '100%' }}
            onClick={this.closePopup}
          >
            {t('campaigns', 'shop-of-the-year.close-popup')}
          </Button>
        </div>
      </React.Fragment>
    );
  }

  closePopup() {
    const { onRequestClose, sotyVotePopupClosed } = this.props;
    sotyVotePopupClosed();
    onRequestClose();
  }

  render() {
    const { voted, shop, sotyVotePopupOpened } = this.props;

    return (
      <Modal
        isOpen
        shouldShowCloseIcon
        onRequestClose={() => this.closePopup()}
        onAfterOpen={sotyVotePopupOpened}
        width="320px"
      >
        <div className={styles.block}>
          <StoreLogo
            variant={LOGO_VARIANTS.SOTY}
            name={shop.name}
            storeId={shop.id}
            hasImage={shop.hasLogo}
          />
          {!voted ? this.getModeVoting() : this.getModeHasVoted()}
        </div>
      </Modal>
    );
  }
}

const { object, shape, func, string, number, bool, arrayOf } = PropTypes;

VotePopup.propTypes = {
  shop: shape({
    id: number,
    name: string,
    featured: bool,
    logo: string,
  }).isRequired,
  nominationCategory: shape({
    id: number,
    name: string,
    logo: string,
  }).isRequired,
  charity: shape({
    name: string,
    description: string,
    logo: string,
  }),
  year: number.isRequired,
  onRequestClose: func, // eslint-disable-line react/forbid-prop-types
  sotyVote: func,
  sotyVotePopupClosed: func,
  sotyVotePopupOpened: func,
  user: object, // eslint-disable-line react/forbid-prop-types
  token: object, // eslint-disable-line react/forbid-prop-types
  voting: bool,
  voted: bool,
  errors: object, // eslint-disable-line react/forbid-prop-types
  history: arrayOf(
    shape({
      category: shape({
        id: number,
      }),
      store: shape({
        name: string,
        id: number,
      }),
    })
  ),
};

VotePopup.defaultProps = {
  onRequestClose: () => {},
  sotyVote: () => {},
  sotyVotePopupClosed: () => {},
  sotyVotePopupOpened: () => {},
  charity: null,
  user: undefined,
  token: undefined,
  voting: false,
  voted: false,
  errors: {},
  history: [],
};

VotePopup.contextType = AppContext;

export default connect(
  state => ({ ...state.areas.soty.soty_vote_popup, user: state.areas.account }),
  actions
)(VotePopup);
